import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { BotIcon, LoaderIcon, ToggleRightIcon, TriangleAlertIcon } from "lucide-react";
import type {
  FeatureFlagAdminState,
  FeatureFlagCapabilityKey,
  FeatureFlagIssue,
  FeatureFlagPatch,
  ModelSelection,
} from "@t3tools/contracts";

import { readLocalApi } from "../../localApi";
import {
  getServerConfigUpdatedNotification,
  useServerConfigUpdatedSubscription,
} from "../../rpc/serverState";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { toastManager } from "../ui/toast";
import {
  buildCapabilityRows,
  capabilityPatch,
  fastModeDescriptor,
  fileIssues,
  findModel,
  issuesFor,
  reasoningEffortDescriptor,
  resetPatch,
  selectedFastMode,
  selectedReasoningEffort,
  withFastMode,
  withModel,
  withReasoningEffort,
} from "./CapabilitiesSettings.logic";
import {
  SettingResetButton,
  SettingsPageContainer,
  SettingsRow,
  SettingsSection,
} from "./settingsLayout";

const CAPABILITY_TEXT: Record<
  FeatureFlagCapabilityKey,
  { label: MessageDescriptor; description: MessageDescriptor }
> = {
  voiceInput: {
    label: msg`Voice input`,
    description: msg`Microphone button in the composer; recordings are transcribed through docparser.`,
  },
  deepWork: {
    label: msg`Deep Work`,
    description: msg`Deep Work toggle in the composer.`,
  },
};

function IssueNotice({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 border-t border-border/60 px-4 py-3 text-warning text-xs first:border-t-0 sm:px-5">
      <TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0" />
      <div className="min-w-0 space-y-1">{children}</div>
    </div>
  );
}

function IssueDetail({ issue }: { issue: FeatureFlagIssue }) {
  return (
    <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-muted-foreground">
      {issue.detail}
    </pre>
  );
}

export function CapabilitiesSettings() {
  const { t, i18n } = useLingui();
  const [state, setState] = useState<FeatureFlagAdminState | null>(null);
  const [pending, setPending] = useState(false);
  const latestRequest = useRef(0);
  const seenNotificationId = useRef(getServerConfigUpdatedNotification()?.id ?? 0);

  const refresh = useCallback(async () => {
    const api = readLocalApi();
    if (!api) return;
    const request = ++latestRequest.current;
    try {
      const next = await api.server.getFeatureFlagsAdmin();
      if (request === latestRequest.current) setState(next);
    } catch (error) {
      toastManager.add({
        title: t`Unable to load capabilities`,
        description: error instanceof Error ? error.message : t`The server did not answer.`,
        type: "error",
      });
    }
  }, [t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useServerConfigUpdatedSubscription((notification) => {
    if (notification.id <= seenNotificationId.current) return;
    seenNotificationId.current = notification.id;
    if (notification.source !== "snapshot" && notification.source !== "agentStatus") return;
    void refresh();
  });

  const update = useCallback(
    async (patch: FeatureFlagPatch) => {
      const api = readLocalApi();
      if (!api) return;
      setPending(true);
      const request = ++latestRequest.current;
      try {
        const next = await api.server.updateFeatureFlags(patch);
        if (request === latestRequest.current) setState(next);
      } catch (error) {
        toastManager.add({
          title: t`Unable to update capability`,
          description:
            error instanceof Error ? error.message : t`The feature flag file was not updated.`,
          type: "error",
        });
      } finally {
        setPending(false);
      }
    },
    [t],
  );

  if (!state) {
    return (
      <div className="flex flex-1 items-center justify-center text-muted-foreground/40">
        <LoaderIcon className="size-4 animate-spin" />
      </div>
    );
  }

  const capabilityRows = buildCapabilityRows(state);
  const overridden = capabilityRows.some((row) => row.origin === "override");
  const invalidFileIssues = fileIssues(state.issues);
  const model = state.values.model;
  const selectedModel = findModel(state.models, model.model);
  const effortDescriptor = reasoningEffortDescriptor(selectedModel);
  const effort = selectedReasoningEffort(model, selectedModel);
  const fastMode = fastModeDescriptor(selectedModel);
  const updateModel = (next: ModelSelection) => void update({ model: next });

  return (
    <SettingsPageContainer>
      {invalidFileIssues.length > 0 ? (
        <SettingsSection title={t`Team flag file`} icon={<TriangleAlertIcon className="size-4" />}>
          {invalidFileIssues.map((issue) => (
            <IssueNotice key={issue.detail}>
              <p>
                <Trans>
                  The team flag file is invalid; the last valid values stay in force until it is
                  fixed or the next change here rewrites it.
                </Trans>
              </p>
              <IssueDetail issue={issue} />
            </IssueNotice>
          ))}
        </SettingsSection>
      ) : null}

      <SettingsSection title={t`User capabilities`} icon={<ToggleRightIcon className="size-4" />}>
        {overridden ? (
          <div className="px-4 py-3 text-muted-foreground text-sm sm:px-5">
            <Trans>
              Capabilities are pinned by the <code>T3CODE_FEATURE_FLAGS</code> override of this dev
              run; the flag files take over once the override is removed.
            </Trans>
          </div>
        ) : null}
        {capabilityRows.map((row) => {
          const text = CAPABILITY_TEXT[row.key];
          const label = i18n._(text.label);
          return (
            <SettingsRow
              key={row.key}
              title={label}
              description={i18n._(text.description)}
              status={
                issuesFor(state.issues, row.key).length > 0 ? (
                  <span className="text-warning">
                    <Trans>The team value is invalid; the image value applies.</Trans>
                  </span>
                ) : (
                  <code className="text-muted-foreground text-xs">{row.fileKey}</code>
                )
              }
              control={
                <Switch
                  checked={row.enabled}
                  disabled={row.origin === "override" || pending}
                  onCheckedChange={(checked) => void update(capabilityPatch(row.key, checked))}
                  aria-label={label}
                />
              }
            />
          );
        })}
      </SettingsSection>

      <SettingsSection title={t`Agent`} icon={<BotIcon className="size-4" />}>
        {issuesFor(state.issues, "model").map((issue) => (
          <IssueNotice key={issue.detail}>
            <p>
              <Trans>The team model value is invalid; the image value applies.</Trans>
            </p>
            <IssueDetail issue={issue} />
          </IssueNotice>
        ))}
        {selectedModel ? null : (
          <IssueNotice>
            <p>
              <Trans>
                The Codex instance does not offer the configured model <code>{model.model}</code>.
              </Trans>
            </p>
          </IssueNotice>
        )}
        <SettingsRow
          title={t`Model`}
          description={t`Codex model every turn runs with.`}
          resetAction={
            state.origins.model === "team" ? (
              <SettingResetButton
                label={t`model`}
                onClick={() => void update(resetPatch("model"))}
              />
            ) : null
          }
          control={
            <Select
              value={model.model}
              disabled={pending || state.models.length === 0}
              onValueChange={(value) => {
                const next = typeof value === "string" ? findModel(state.models, value) : undefined;
                if (next && next.slug !== model.model) updateModel(withModel(model, next));
              }}
            >
              <SelectTrigger className="w-full sm:w-56" aria-label={t`Model`}>
                <SelectValue>{selectedModel?.name ?? model.model}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                {state.models.map((option) => (
                  <SelectItem hideIndicator key={option.slug} value={option.slug}>
                    {option.name}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          }
        />
        {selectedModel && effortDescriptor ? (
          <SettingsRow
            title={t`Reasoning effort`}
            description={t`How much the model reasons before it answers.`}
            control={
              <Select
                value={effort ?? ""}
                disabled={pending}
                onValueChange={(value) => {
                  if (typeof value === "string" && value !== effort) {
                    updateModel(withReasoningEffort(model, selectedModel, value));
                  }
                }}
              >
                <SelectTrigger className="w-full sm:w-56" aria-label={t`Reasoning effort`}>
                  <SelectValue>
                    {effortDescriptor.options.find((option) => option.id === effort)?.label ??
                      effort}
                  </SelectValue>
                </SelectTrigger>
                <SelectPopup align="end" alignItemWithTrigger={false}>
                  {effortDescriptor.options.map((option) => (
                    <SelectItem hideIndicator key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>
            }
          />
        ) : null}
        {selectedModel && fastMode ? (
          <SettingsRow
            title={t`Fast mode`}
            description={t`Faster responses at a higher usage cost.`}
            control={
              <Switch
                checked={selectedFastMode(model)}
                disabled={pending}
                onCheckedChange={(checked) =>
                  updateModel(withFastMode(model, selectedModel, Boolean(checked)))
                }
                aria-label={t`Fast mode`}
              />
            }
          />
        ) : null}
      </SettingsSection>
    </SettingsPageContainer>
  );
}
